import { useCallback, useEffect, useState } from "react";
import {
  calculateFoodBasket,
  fetchFoodBaskets,
  fetchFoodSources,
  fetchFoodStores,
} from "../../../../api/foodApi";
import { FOOD_SEEDED_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";
import { formatCop } from "../../../../constants/food";

const FoodBasketCalculationPage = ({ onClose }) => {
  const [baskets, setBaskets] = useState([]);
  const [sources, setSources] = useState([]);
  const [stores, setStores] = useState([]);
  const [basketId, setBasketId] = useState("");
  const [sourceKey, setSourceKey] = useState("ARA_COLOMBIA");
  const [storeId, setStoreId] = useState("");
  const [city, setCity] = useState("Medellín");
  const [freshnessWindowHours, setFreshnessWindowHours] = useState(72);
  const [preferPromotionPrice, setPreferPromotionPrice] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadMeta = useCallback(async () => {
    try {
      const [basketsData, sourcesData, storesData] = await Promise.all([
        fetchFoodBaskets(),
        fetchFoodSources(),
        fetchFoodStores(),
      ]);
      const list = basketsData.baskets || [];
      setBaskets(list);
      setSources(sourcesData.sources || []);
      setStores(storesData.stores || []);
      const defaultBasket = list.find((b) => b.isDefault) || list[0];
      if (defaultBasket) setBasketId(defaultBasket.id);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load basket metadata.");
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const handleCalculate = async () => {
    try {
      setRunning(true);
      setErrorMessage("");
      const payload = await calculateFoodBasket({
        basketId: basketId || undefined,
        sourceKey: sourceKey || undefined,
        storeId: storeId || undefined,
        city: city || undefined,
        freshnessWindowHours: Number(freshnessWindowHours) || 72,
        preferPromotionPrice,
        dryRun,
      });
      setResult(payload);
    } catch (error) {
      setErrorMessage(error.message || "Basket calculation failed.");
    } finally {
      setRunning(false);
    }
  };

  const missingItems = (result?.items || []).filter((item) => item.matchStatus === "MISSING");

  return (
    <div className="admin-page food-basket-calculation-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>Basket Calculation</h1>
          <p className="muted">Calculate household basket cost from normalized observations.</p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card food-basket-calculation-page__controls">
        <div className="food-normalization-page__control-row">
          <label>
            Basket
            <select value={basketId} onChange={(e) => setBasketId(e.target.value)}>
              {baskets.map((basket) => (
                <option key={basket.id} value={basket.id}>
                  {basket.basketName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Source
            <select value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
              <option value="">—</option>
              {FOOD_SEEDED_SOURCE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </label>
          <label>
            Store
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              <option value="">All stores in source</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.storeName}
                </option>
              ))}
            </select>
          </label>
          <label>
            City
            <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            Freshness window (hours)
            <input
              type="number"
              min={1}
              max={720}
              value={freshnessWindowHours}
              onChange={(e) => setFreshnessWindowHours(e.target.value)}
            />
          </label>
          <label className="food-normalization-page__checkbox">
            <input
              type="checkbox"
              checked={preferPromotionPrice}
              onChange={(e) => setPreferPromotionPrice(e.target.checked)}
            />
            Prefer promotion price
          </label>
          <label className="food-normalization-page__checkbox">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={running}
            onClick={handleCalculate}
          >
            {running ? "Calculating…" : dryRun ? "Dry run" : "Calculate & save"}
          </button>
        </div>
      </section>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}

      {result ? (
        <section className="panel-card">
          <h2>Result</h2>
          <p>
            <strong>{result.basketName}</strong> — {formatCop(result.totalCostCop)} ·{" "}
            {result.status}
          </p>
          <p className="muted">
            Coverage: {result.totalItemsCount - result.missingItemsCount} /{" "}
            {result.totalItemsCount} ({Math.round((result.coverageRatio || 0) * 100)}%)
          </p>
          {result.dryRun ? <p className="muted">Dry run — no snapshot saved.</p> : null}
          {missingItems.length > 0 ? (
            <>
              <h3>Missing items</h3>
              <ul>
                {missingItems.map((item) => (
                  <li key={item.basketItemId}>{item.productName || item.basketItem}</li>
                ))}
              </ul>
            </>
          ) : null}
          <pre className="food-normalization-page__summary">
            {JSON.stringify(result, null, 2)}
          </pre>
        </section>
      ) : null}

      {sources.length > 0 ? (
        <p className="muted">
          Registered sources: {sources.map((s) => s.sourceName).join(", ")}
        </p>
      ) : null}
    </div>
  );
};

export default FoodBasketCalculationPage;
